/**
 * Formatting utilities for display
 */

/**
 * Format currency for display in Swedish locale
 * @param amount - The amount to format
 * @returns Formatted string like "1 234 kr"
 */
export const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

/**
 * Normalize stray line-break characters in user-entered text to plain `\n`.
 *
 * Text pasted from PDFs, Word, macOS apps, etc. often carries line-break
 * characters that a `<textarea>` does NOT render as newlines (so they're
 * invisible while editing) but that CSS `white-space: pre-line` / `pre-wrap`
 * DOES render as forced breaks (so they show up in tooltips and read-only
 * views). Because the characters survive a textarea round-trip untouched,
 * re-saving the field never clears them. Collapsing them to real `\n` makes
 * the breaks visible/editable and keeps display consistent across views.
 *
 * Handles: CRLF and lone CR, U+0085 (NEL), U+2028 (line separator),
 * U+2029 (paragraph separator), U+000B (vertical tab), U+000C (form feed).
 *
 * @param text - Raw user-entered text
 * @returns Text with all exotic line-break characters replaced by `\n`
 */
export const normalizeLineBreaks = (text: string): string => {
    return text.replace(/\r\n?|\u0085|\u2028|\u2029|[\u000B\u000C]/g, "\n");
};

/**
 * Format date for display (YYYY-MM-DD to Swedish date format)
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted string like "15/01 2024"
 */
export const formatDisplayDate = (dateString: string): string => {
    try {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month} ${year}`;
    } catch {
        return dateString;
    }
};
